import React, { useMemo, useRef } from 'react';
import { Sun, Moon, Command } from 'lucide-react';
import { useTheme } from './components/ThemeContext';
import Background from './components/Background';
import Profile from './components/Profile';
import Experience from './components/Experience';
import Achievements from './components/Achievements';
import Availability from './components/Availability';
import Featured from './components/Featured';
import Projects from './components/Projects';
import GithubActivity from './components/GithubActivity';
import TechStack from './components/TechStack';
import Footer from './components/Footer';
import TargetLock from './components/TargetLock';
import Magnetic from './components/Magnetic';
import CommandPalette from './components/CommandPalette';
import useSmoothScroll from './components/useSmoothScroll';

const column = 'relative no-scrollbar lg:h-screen lg:overflow-y-auto lg:overscroll-contain';

const App = () => {
    const { isDarkMode, toggleTheme } = useTheme();
    const leftRef = useRef(null);
    const middleRef = useRef(null);
    const rightRef = useRef(null);
    const columns = useMemo(() => [leftRef, middleRef, rightRef], []);
    useSmoothScroll(columns);

    return (
        <>
            <Background />
            <TargetLock />
            <CommandPalette />

            <Magnetic className="fixed right-[60px] top-4 z-50" strength={0.3}>
                <button
                    type="button"
                    onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
                    aria-label="Open command palette"
                    data-say="Command palette"
                    className="inline-flex h-9 items-center gap-1.5 rounded-full border bg-background/70 px-3 font-mono text-[11px] text-muted-foreground backdrop-blur-md transition-colors hover:bg-accent hover:text-foreground"
                >
                    <Command size={12} />
                    K
                </button>
            </Magnetic>

            <Magnetic className="fixed right-4 top-4 z-50" strength={0.35}>
                <button
                    type="button"
                    onClick={toggleTheme}
                    data-say={isDarkMode ? 'Lights on ☀️' : 'Lights off 🌙'}
                    aria-label={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border bg-background/70 text-muted-foreground backdrop-blur-md transition-colors hover:bg-accent hover:text-foreground"
                >
                    {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
                </button>
            </Magnetic>

            <main className="min-h-screen lg:grid lg:h-screen lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,1fr)] lg:overflow-hidden">
                {/* Left: who I am */}
                <aside ref={leftRef} className={`${column} lg:border-r lg:pb-[30vh]`}>
                    <Profile />
                    <Experience />
                    <Achievements />
                    <Availability />
                </aside>

                {/* Middle: work */}
                <div ref={middleRef} className={`${column} min-w-0`}>
                    <Featured />
                    <Projects />
                    <Footer />
                </div>

                {/* Right: activity and stack */}
                <aside ref={rightRef} className={`${column} lg:border-l`}>
                    <GithubActivity />
                    <TechStack />
                </aside>
            </main>
        </>
    );
};

export default App;
